import { expenseNextRenewal, expenseRenewsPaid } from '@/function/expense/expense.state';
import expenseRenewalPeriods from '@/function/expense/expense.renewal.periods';
import type { TypeExpense } from '@/types/expense/types.expense';
import type { TypeExpensePayment } from '@/types/expense/types.expense.calendar';
import { addDaysToDayKey, addMonthsToDayKey, isDayKey, monthsBetweenKeys } from '@/utils/date/day.key';

const DUE_SLACK_DAYS = 62;

const payDayOf = (expense: Pick<TypeExpense, 'dueDate' | 'documentDate'>): string =>
    (isDayKey(expense.dueDate) ? expense.dueDate : expense.documentDate);

const renewalPayDay = (expense: Pick<TypeExpense, 'dueDate' | 'documentDate'>, period: string): string =>
    (isDayKey(expense.dueDate) ? addMonthsToDayKey(expense.dueDate, monthsBetweenKeys(expense.documentDate, period)) : period);

const within = (date: string, from: string, to: string): boolean => isDayKey(date) && date >= from && date < to;

export function expenseCalendarPayments(expenses: readonly TypeExpense[], from: string, to: string): TypeExpensePayment[] {
    if (!isDayKey(from) || !isDayKey(to) || from >= to) return [];
    const payments: TypeExpensePayment[] = [];
    const seen = new Set<string>();

    for (const expense of expenses) {
        if (seen.has(expense.id)) continue;
        seen.add(expense.id);

        if (expense.stage === 'planned') {
            if (within(expense.plannedDate, from, to)) {
                payments.push({ key: expense.id, expense, date: expense.plannedDate, period: '', projected: true, autoPaid: false, planned: true });
            }
            continue;
        }

        const day = payDayOf(expense);
        const counts = !expense.paidDate || expense.recurrence !== 'none';
        if (counts && within(day, from, to)) {
            payments.push({ key: expense.id, expense, date: day, period: expense.documentDate, projected: false, autoPaid: false, planned: false });
        }

        const next = expenseNextRenewal(expense);
        if (!next) continue;
        const autoPaid = expenseRenewsPaid(expense);
        for (const period of expenseRenewalPeriods(expense, addDaysToDayKey(to, DUE_SLACK_DAYS))) {
            if (period < next) continue;
            const date = renewalPayDay(expense, period);
            if (!within(date, from, to)) continue;
            payments.push({ key: `${expense.id}@${period}`, expense, date, period, projected: true, autoPaid, planned: false });
        }
    }

    return payments.sort((a, b) => a.date.localeCompare(b.date) || b.expense.totalCents - a.expense.totalCents || a.key.localeCompare(b.key));
}

export default expenseCalendarPayments;
